import { cross3, sub3 } from './vector-math.js';

/**
 * The module's single degeneracy/renderability threshold, applied
 * scale-relatively (against the triangle's own shortest edge squared) by
 * `triangleRenderability` below — the same 1e-9-against-a-local-characteristic-
 * length stance `INSET_AREA_EPSILON` and `SCALE_AREA_EPSILON` already take for
 * the geometry *they* construct, now also governing the two places that
 * previously used a bare *absolute* 1e-9 on an unnormalized cross product
 * (`faceNormal` and `flatFaceNormal`). Absolute was wrong in both directions:
 * it called every triangle in a millimeter-scale model degenerate (a 4e-5-edge
 * triangle has a legitimate ~1e-9 area and a perfectly good normal), and it
 * accepted an arbitrarily thin sliver at kilometer scale.
 */
const TRIANGLE_AREA_EPSILON = 1e-9;

/** The shortest of a triangle's 3 edges — the "local, per-face characteristic length" every scale-relative threshold in this module measures against, rather than a region-wide or absolute one. */
function shortestEdgeLength(positions) {
  const [a, b, c] = positions;
  return Math.min(
    Math.hypot(...sub3(b, a)),
    Math.hypot(...sub3(c, b)),
    Math.hypot(...sub3(a, c)),
  );
}

/**
 * THE renderability rule: the one predicate that decides whether a triangle
 * is a real, renderable surface or a degenerate one, shared by every caller
 * that needs that verdict — `faceNormal` (can a topology operation use this
 * face?), `flatFaceNormal` (does `buildGeometryFromTopology` give this face a
 * real shading normal, or the zero vector?), and subdivision's own validation
 * of the faces it constructs. Previously those three disagreed: subdivision
 * could mint a face that `buildGeometryFromTopology` then rendered with a
 * zero normal and that `faceNormal` would refuse to operate on, and could
 * reject a sliver the renderer handled fine. Having one rule in one place is
 * what makes "validated" and "renderable" the same statement rather than two
 * that happen to be spelled similarly.
 *
 * `rounded` selects the precision the rule is *evaluated* at — the rule
 * itself (scale-relative threshold, strictly positive area, finite result) is
 * the same either way:
 *
 * - `true` (default) rounds every coordinate to float32 with `Math.fround`
 *   first, mirroring exactly what `buildGeometryFromTopology` stores in its
 *   `Float32Array`. This is the right question for anything asking "will this
 *   render" — `flatFaceNormal`, and the validation of every face subdivision
 *   constructs. It catches two failure modes float64 hides: a position past
 *   float32's ~3.4e38 range (`Math.fround` returns a non-finite value, so the
 *   cross length is non-finite), and a triangle far enough from the origin
 *   that its corners round together even though they were distinct at float64.
 * - `false` measures the authored geometry at this module's own float64
 *   working precision, which is the right question for `faceNormal`: a query
 *   about a face the caller already has, asked so a topology operation can
 *   decide what to do with it. Rounding there would reclassify legitimately
 *   authored geometry — a 1000-unit-long sliver 1e-6 thick is perfectly well
 *   defined at float64 and its two ends collapse onto each other at float32 —
 *   and silently change which faces region discovery walks across, which is a
 *   much broader claim than "this face has no renderable area".
 *
 * `crossLength > 0` is required explicitly rather than relying on the
 * threshold comparison: a triangle with two coincident corners has a
 * shortest edge of 0, hence a threshold of 0, and `0 >= 0` would otherwise
 * declare a provably zero-area triangle renderable.
 */
export function triangleRenderability(positions, { rounded = true } = {}) {
  const [a, b, c] = rounded
    ? positions.map((p) => [Math.fround(p[0]), Math.fround(p[1]), Math.fround(p[2])])
    : positions;
  const cross = cross3(sub3(b, a), sub3(c, a));
  const crossLength = Math.hypot(...cross);
  const threshold = TRIANGLE_AREA_EPSILON * shortestEdgeLength([a, b, c]) ** 2;
  // `reason` exists so callers can say accurately *why* a face is unusable.
  // "Degenerate (zero area)" is actively misleading for a triangle whose
  // coordinates are so large that its own cross product overflows to
  // Infinity — that face has more extent than can be represented, not less.
  const reason = !Number.isFinite(crossLength)
    ? 'non-finite'
    : (crossLength > 0 && crossLength >= threshold ? null : 'zero-area');
  return { cross, crossLength, renderable: reason === null, reason };
}

/** The marker every "this face has no usable normal" error carries, so `coplanarRegion` can skip such a neighbor without resorting to matching on message text — which would also swallow a structurally corrupt face (one referencing an unknown vertex), a real bug elsewhere in the topology rather than a legitimate "doesn't qualify" case. */
export const FACE_NOT_RENDERABLE = 'FACE_NOT_RENDERABLE';

export function notRenderableError(faceId, reason) {
  const error = new Error(
    reason === 'non-finite'
      ? `face '${faceId}' has non-finite extent (its coordinates overflow) and has no normal`
      : `face '${faceId}' is degenerate (zero area) and has no normal`,
  );
  error.code = FACE_NOT_RENDERABLE;
  return error;
}

/** Reject a newly-constructed triangle that would not be renderable — the shared `triangleRenderability` rule (float32, scale-relative to the child's *own* shortest edge), so a face this accepts is exactly a face `buildGeometryFromTopology` gives a real shading normal to and `faceNormal` will operate on. Taking the scale from the child itself, rather than from a threshold passed in by the caller, is also what stops a degenerate *neighbour* from collapsing the threshold to zero and waving its own zero-area children through. */
export function assertRenderableTriangle(commandName, faceLabel, positions) {
  if (!triangleRenderability(positions).renderable) {
    throw new Error(
      `${commandName} would produce a degenerate or non-finite ${faceLabel} at the float32 precision the mesh actually renders at`,
    );
  }
}
